import type { PublicJobcardView, PublicQuotationDecisionRequest } from '@garage/shared'

/**
 * Fetch client for the public portal endpoints.
 *
 * Separate from the staff API so the portal never tries to load staff routes.
 */

export class PublicApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'PublicApiError'
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const hasJsonBody = init?.body !== undefined && init.body !== null && !(init.body instanceof FormData)

  const response = await fetch(`/api/public${path}`, {
    ...init,
    headers: {
      ...(hasJsonBody ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  })

  if (!response.ok) {
    const statusText = response.statusText || `HTTP ${response.status}`
    throw new PublicApiError(response.status, statusText)
  }

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

export const publicApi = {
  /**
   * Fetch the jobcard view for a token.
   *
   * 404 when the token is invalid or regenerated.
   */
  jobcard: (token: string) => request<PublicJobcardView>(`/${token}`),

  /**
   * Approve or decline the quotation currently awaiting the customer's
   * approval. Returns the refreshed view — its new status carries the result.
   */
  decideQuotation: (token: string, body: PublicQuotationDecisionRequest) =>
    request<PublicJobcardView>(`/${token}/quotation-decision`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * Preview PDF for a document in the browser.
   *
   * The browser opens this in the PDF viewer; link as an href.
   */
  documentPdfUrl: (token: string, documentId: string) =>
    `/api/public/${token}/documents/${documentId}/pdf`,

  /**
   * Download PDF for a document (forced download).
   *
   * Returns attachment disposition instead of inline.
   */
  documentPdfDownloadUrl: (token: string, documentId: string) =>
    `/api/public/${token}/documents/${documentId}/pdf?download=1`,

  /**
   * Get the URL for an inspection item photo.
   * Optionally request a webp thumbnail: ?w=128, ?w=320, or ?w=1600.
   */
  inspectionItemPhotoUrl: (token: string, itemId: string, width?: 128 | 320 | 1600) => {
    const path = `/api/public/${encodeURIComponent(token)}/inspection/items/${encodeURIComponent(itemId)}/photo`
    return width !== undefined ? `${path}?w=${width}` : path
  },

  /**
   * Get the URL for an inspection overview photo (whole vehicle angle).
   * Optionally request a webp thumbnail: ?w=128, ?w=320, or ?w=1600.
   */
  inspectionOverviewPhotoUrl: (token: string, angle: string, width?: 128 | 320 | 1600) => {
    const path = `/api/public/${encodeURIComponent(token)}/inspection/overview-photos/${encodeURIComponent(angle)}`
    return width !== undefined ? `${path}?w=${width}` : path
  },
}
